import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Loader2, Send, Brain, AlertCircle, ArrowRight, Zap, Users } from 'lucide-react';
import { Alert, AlertDescription } from './ui/alert';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';

interface IntelligentChatAgentProps {
  userId: string;
  initialProjectId: string;
  onProjectSwitch?: (newProjectId: string) => void;
}

interface ContextDetails {
  interests: string[];
  preferences?: string[];
  recentInteractions?: Array<{
    text: string;
    timestamp: string;
    interactionId: string;
  }>;
  projectMetadata?: {
    projectName?: string;
    creationDate?: string;
    memberCount?: number;
  };
}

interface ContextAnalysis {
  suggested_contexts: string[];
  confidence_score: number;
  reasoning: string;
  current_project_relevance: number;
}

interface Project {
  projectId: string;
  projectName?: string;
  creationDate?: string;
  interactionCount: number;
  lastActivity?: string;
}

const IntelligentChatAgent: React.FC<IntelligentChatAgentProps> = ({
  userId,
  initialProjectId,
  onProjectSwitch
}) => {
  const [query, setQuery] = useState('');
  const [response, setResponse] = useState('');
  const [currentProjectId, setCurrentProjectId] = useState(initialProjectId);
  const [contextData, setContextData] = useState<ContextDetails | null>(null);
  const [userProjects, setUserProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isContextLoading, setIsContextLoading] = useState(false);
  const [contextSuggestion, setContextSuggestion] = useState<ContextAnalysis | null>(null);
  const [showSuggestionDialog, setShowSuggestionDialog] = useState(false);

  // Fetch user projects on mount
  useEffect(() => {
    fetchUserProjects();
  }, [userId]);

  // Fetch context when project changes
  useEffect(() => {
    fetchContext();
  }, [currentProjectId, userId]);

  // Notify parent of project switch
  useEffect(() => {
    if (onProjectSwitch) {
      onProjectSwitch(currentProjectId);
    }
  }, [currentProjectId, onProjectSwitch]);

  const fetchUserProjects = async () => {
    try {
      const response = await fetch('/api/knowledge-graph/projects', {
        method: 'GET',
        headers: { 
          'X-User-ID': userId
        },
      });

      if (response.ok) {
        const data = await response.json();
        setUserProjects(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching user projects:', error);
    }
  };

  const fetchContext = async () => {
    if (!userId || !currentProjectId) return;
    
    setIsContextLoading(true);
    setError(null);
    
    try {
      const response = await fetch('/api/knowledge-graph/tools/getContextDetails', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-User-ID': userId
        },
        body: JSON.stringify({ 
          projectId: currentProjectId,
          includeRecent: true,
          recentLimit: 3
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch context');
      }

      const data = await response.json();
      setContextData(data.data);
    } catch (error) {
      console.error('Error fetching context:', error);
      setError(error instanceof Error ? error.message : 'Failed to fetch context');
    } finally {
      setIsContextLoading(false);
    }
  };

  const analyzeContextNeeds = async (userQuery: string): Promise<ContextAnalysis> => {
    try {
      const response = await fetch('/api/knowledge-graph/tools/analyzeContextNeeds', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-User-ID': userId
        },
        body: JSON.stringify({
          currentProjectId,
          userQuery
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to analyze context needs');
      }

      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error('Error analyzing context needs:', error);
      return {
        suggested_contexts: [],
        confidence_score: 0,
        reasoning: 'Analysis failed',
        current_project_relevance: 0.5
      };
    }
  };

  const handleSendMessage = async () => {
    if (!query.trim() || isLoading) return;

    setIsLoading(true);
    setError(null);
    setContextSuggestion(null);

    try {
      // 1. Analyze the query for context switching needs
      const analysis = await analyzeContextNeeds(query);
      let finalContext = contextData;
      let responsePreamble = '';
      let workingProjectId = currentProjectId;

      // 2. Decide whether to switch, suggest, or stay
      if (analysis.confidence_score > 0.8 && analysis.suggested_contexts.length > 0) {
        // High confidence - offer to switch automatically
        setContextSuggestion(analysis);
        setShowSuggestionDialog(true);
        setIsLoading(false);
        return;
      } else if (analysis.confidence_score > 0.3 && analysis.suggested_contexts.length > 0) {
        // Medium confidence - mention but don't force switch
        const suggestedProject = userProjects.find(p => p.projectId === analysis.suggested_contexts[0]);
        responsePreamble = `💡 Note: This query might be related to "${suggestedProject?.projectName || analysis.suggested_contexts[0]}". `;
      }

      // 3. Ingest interaction into current project
      await fetch('/api/knowledge-graph/interactions', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-User-ID': userId
        },
        body: JSON.stringify({
          projectId: workingProjectId,
          text: query,
          timestamp: new Date().toISOString()
        }),
      });

      // 4. Build context-enriched prompt
      const interests = finalContext?.interests?.join(', ') || 'none identified';
      const preferences = finalContext?.preferences?.join(', ') || 'none specified';
      const projectInfo = finalContext?.projectMetadata?.projectName || workingProjectId;
      
      const contextPrompt = `User query: "${query}"

Context from project "${projectInfo}":
- Topics of interest: ${interests}
- User preferences: ${preferences}
- Context relevance score: ${analysis.current_project_relevance.toFixed(2)}
- Analysis: ${analysis.reasoning}

Please provide a relevant response based on this context.`;

      // Here you would integrate with your existing LLM service
      setResponse(`${responsePreamble}Context-enriched prompt generated:

${contextPrompt}

[In a real implementation, this would be sent to your LLM API and the actual response would be displayed here]`);

      // 5. Refresh context after interaction
      await fetchContext();
    } catch (error) {
      console.error('Error in intelligent agent:', error);
      setError(error instanceof Error ? error.message : 'Failed to process message');
    } finally {
      setIsLoading(false);
    }
  };

  const handleContextSwitch = async (newProjectId: string) => {
    setCurrentProjectId(newProjectId);
    setShowSuggestionDialog(false);
    
    // Continue with the original query in the new context
    setTimeout(() => {
      handleSendMessage();
    }, 100);
  };

  const handleDismissSuggestion = () => {
    setShowSuggestionDialog(false);
    setContextSuggestion(null);
    
    // Continue with original context
    setTimeout(() => {
      handleSendMessage();
    }, 100);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const currentProject = userProjects.find(p => p.projectId === currentProjectId);
  const suggestedProject = contextSuggestion?.suggested_contexts[0] 
    ? userProjects.find(p => p.projectId === contextSuggestion.suggested_contexts[0])
    : null;

  return (
    <div className="space-y-4">
      {/* Project Selector */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-sm">
            <Users className="h-4 w-4" />
            Current Project
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Select value={currentProjectId} onValueChange={setCurrentProjectId}>
            <SelectTrigger>
              <SelectValue placeholder="Select a project" />
            </SelectTrigger>
            <SelectContent>
              {userProjects.map((project) => (
                <SelectItem key={project.projectId} value={project.projectId}>
                  <div className="flex flex-col">
                    <span>{project.projectName || project.projectId}</span>
                    <span className="text-xs text-muted-foreground">
                      {project.interactionCount} interactions
                      {project.lastActivity && (
                        <> • Last: {new Date(project.lastActivity).toLocaleDateString()}</>
                      )}
                    </span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* Context Display */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-sm">
            <Brain className="h-4 w-4" />
            Project Context: {currentProject?.projectName || currentProjectId}
            <Badge variant="outline" className="ml-auto">
              <Zap className="h-3 w-3 mr-1" />
              AI Enhanced
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {isContextLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-3 w-3 animate-spin" />
              Loading context...
            </div>
          ) : contextData ? (
            <>
              {contextData.interests && contextData.interests.length > 0 && (
                <div>
                  <label className="text-xs font-medium text-muted-foreground">
                    Topics of Interest:
                  </label>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {contextData.interests.map((interest, index) => (
                      <Badge key={index} variant="secondary" className="text-xs">
                        {interest}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              
              {contextData.preferences && contextData.preferences.length > 0 && (
                <div>
                  <label className="text-xs font-medium text-muted-foreground">
                    Preferences:
                  </label>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {contextData.preferences.map((pref, index) => (
                      <Badge key={index} variant="outline" className="text-xs">
                        {pref.replace('_', ' ')}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {contextData.recentInteractions && contextData.recentInteractions.length > 0 && (
                <div>
                  <label className="text-xs font-medium text-muted-foreground">
                    Recent Activity:
                  </label>
                  <div className="space-y-1 mt-1">
                    {contextData.recentInteractions.map((interaction, index) => (
                      <div key={index} className="text-xs p-2 bg-muted/30 rounded border-l-2 border-primary/20">
                        <div className="truncate">{interaction.text}</div>
                        <div className="text-muted-foreground text-[10px]">
                          {new Date(interaction.timestamp).toLocaleString()}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-sm text-muted-foreground">
              No context data available for this project
            </div>
          )}
        </CardContent>
      </Card>

      {/* Chat Interface */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Intelligent Context-Aware Chat</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-2">
            <Textarea
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask about this project or mention 'Project Alpha' to switch contexts..."
              className="min-h-[100px]"
              disabled={isLoading}
            />
            <div className="flex justify-between items-center">
              <div className="text-xs text-muted-foreground">
                💡 AI will automatically detect if you need to switch project contexts
              </div>
              <Button 
                onClick={handleSendMessage} 
                disabled={!query.trim() || isLoading}
                size="sm"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="h-3 w-3 animate-spin mr-1" />
                    Analyzing...
                  </>
                ) : (
                  <>
                    <Send className="h-3 w-3 mr-1" />
                    Send
                  </>
                )}
              </Button>
            </div>
          </div>

          {response && (
            <div className="border-t pt-4">
              <h4 className="font-medium mb-2">Response:</h4>
              <div className="bg-muted/50 p-3 rounded-md">
                <pre className="whitespace-pre-wrap text-sm">{response}</pre>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Context Switch Suggestion Dialog */}
      <Dialog open={showSuggestionDialog} onOpenChange={setShowSuggestionDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Brain className="h-5 w-5" />
              Context Switch Detected
            </DialogTitle>
            <DialogDescription>
              I detected that your query might be better answered in a different project context.
            </DialogDescription>
          </DialogHeader>
          
          {contextSuggestion && suggestedProject && (
            <div className="space-y-4">
              <div className="p-3 bg-muted/50 rounded-md">
                <div className="text-sm font-medium mb-1">Analysis:</div>
                <div className="text-sm text-muted-foreground">{contextSuggestion.reasoning}</div>
                <div className="text-xs text-muted-foreground mt-2">
                  Confidence: {(contextSuggestion.confidence_score * 100).toFixed(0)}%
                </div>
              </div>

              <div className="flex items-center justify-center gap-2">
                <Card className="flex-1">
                  <CardContent className="p-3 text-center">
                    <div className="font-medium text-sm">Current</div>
                    <div className="text-xs text-muted-foreground">
                      {currentProject?.projectName || currentProjectId}
                    </div>
                  </CardContent>
                </Card>
                <ArrowRight className="h-4 w-4 text-muted-foreground" />
                <Card className="flex-1">
                  <CardContent className="p-3 text-center">
                    <div className="font-medium text-sm">Suggested</div>
                    <div className="text-xs text-muted-foreground">
                      {suggestedProject.projectName || suggestedProject.projectId}
                    </div>
                  </CardContent>
                </Card>
              </div>

              <div className="flex gap-2">
                <Button 
                  onClick={() => handleContextSwitch(contextSuggestion.suggested_contexts[0])}
                  className="flex-1"
                >
                  Switch & Continue
                </Button>
                <Button 
                  variant="outline" 
                  onClick={handleDismissSuggestion}
                  className="flex-1"
                >
                  Stay Here
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default IntelligentChatAgent; 